import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Factory as FactoryIcon,
  Calculate as CalculateIcon,
  AccessTime as ShiftIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

const Production = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [settings, setSettings] = useState(null);
  const [calculatedOutputs, setCalculatedOutputs] = useState({ oil: 0, cake: 0, sludge: 0 });
  
  const [formData, setFormData] = useState({
    batch_number: '',
    status: 'in_progress',
    production_date: format(new Date(), 'yyyy-MM-dd'),
    shell_input: 0,
    machine_id: '',
    oil_output: 0,
    cake_output: 0,
    sludge_output: 0,
    operator: '',
    shift: '',
    notes: ''
  });

  const BATCH_STATUSES = [
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const SHIFT_OPTIONS = [
    { value: 'general', label: 'General Shift' },
    { value: 'night', label: 'Night Shift' },
    { value: 'e1', label: 'E1 Shift' },
    { value: 'e2', label: 'E2 Shift' },
    { value: 'e3', label: 'E3 Shift' }
  ];

  useEffect(() => {
    fetchBatches();
    fetchProductionSettings();
  }, []);

  useEffect(() => {
    // Auto-calculate outputs when shell_input changes
    if (formData.shell_input > 0 && settings) {
      const shellInput = parseFloat(formData.shell_input) || 0;
      const oil = shellInput * (parseFloat(settings.oil_percentage) / 100);
      const cake = shellInput * (parseFloat(settings.cake_percentage) / 100);
      const sludge = shellInput * (parseFloat(settings.sludge_percentage) / 100);
      
      setCalculatedOutputs({
        oil: parseFloat(oil.toFixed(2)),
        cake: parseFloat(cake.toFixed(2)),
        sludge: parseFloat(sludge.toFixed(2))
      });
      
      setFormData(prev => ({
        ...prev,
        oil_output: parseFloat(oil.toFixed(2)),
        cake_output: parseFloat(cake.toFixed(2)),
        sludge_output: parseFloat(sludge.toFixed(2))
      }));
    } else {
      setCalculatedOutputs({ oil: 0, cake: 0, sludge: 0 });
    }
  }, [formData.shell_input, settings]);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const response = await api.get('/production/batches/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setBatches(data);
    } catch (error) {
      console.error('Error fetching batches:', error);
      toast.error('Failed to fetch production batches');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductionSettings = async () => {
    try {
      const response = await api.get('/production/settings/current/');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Use default values if settings not found
      setSettings({
        oil_percentage: 20,
        cake_percentage: 72,
        sludge_percentage: 8
      });
    }
  };

  const handleOpenDialog = (batch = null) => {
    if (batch) {
      setEditingBatch(batch);
      setFormData({
        batch_number: batch.batch_number || '',
        status: batch.status || 'in_progress',
        production_date: batch.production_date || format(new Date(), 'yyyy-MM-dd'),
        shell_input: parseFloat(batch.shell_input) || 0,
        machine_id: batch.machine_id || '',
        oil_output: parseFloat(batch.oil_output) || 0,
        cake_output: parseFloat(batch.cake_output) || 0,
        sludge_output: parseFloat(batch.sludge_output) || 0,
        operator: batch.operator || '',
        shift: batch.shift || '',
        notes: batch.notes || ''
      });
    } else {
      setEditingBatch(null);
      setFormData({
        batch_number: '',
        status: 'in_progress',
        production_date: format(new Date(), 'yyyy-MM-dd'),
        shell_input: 0,
        machine_id: '',
        oil_output: 0,
        cake_output: 0,
        sludge_output: 0,
        operator: '',
        shift: '',
        notes: ''
      });
      setCalculatedOutputs({ oil: 0, cake: 0, sludge: 0 });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBatch(null);
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'shell_input' || field === 'oil_output' || field === 'cake_output' || field === 'sludge_output' 
        ? parseFloat(value) || 0 
        : value
    }));
  };

  const handleAutoCalculate = () => {
    if (formData.shell_input > 0 && settings) {
      const shellInput = parseFloat(formData.shell_input) || 0;
      const oil = shellInput * (parseFloat(settings.oil_percentage) / 100);
      const cake = shellInput * (parseFloat(settings.cake_percentage) / 100);
      const sludge = shellInput * (parseFloat(settings.sludge_percentage) / 100);
      
      setFormData(prev => ({
        ...prev,
        oil_output: parseFloat(oil.toFixed(2)),
        cake_output: parseFloat(cake.toFixed(2)),
        sludge_output: parseFloat(sludge.toFixed(2))
      }));
      
      toast.success(`Calculated: Oil: ${oil.toFixed(2)}L, Cake: ${cake.toFixed(2)}kg, Sludge: ${sludge.toFixed(2)}kg`);
    } else {
      toast.error('Please enter Shell Input first');
    }
  };

  const handleSubmit = async () => {
    try {
      // Validate
      if (!formData.batch_number) {
        toast.error('Batch Number is required');
        return;
      }
      if (!formData.production_date) {
        toast.error('Production Date is required');
        return;
      }
      if (!formData.shell_input || formData.shell_input <= 0) {
        toast.error('Shell Input must be greater than 0');
        return;
      }

      const submitData = {
        ...formData,
        shell_input: parseFloat(formData.shell_input),
        oil_output: parseFloat(formData.oil_output),
        cake_output: parseFloat(formData.cake_output),
        sludge_output: parseFloat(formData.sludge_output)
      };

      let response;
      if (editingBatch) {
        response = await api.put(`/production/batches/${editingBatch.id}/`, submitData);
        toast.success('Batch updated successfully');
      } else {
        response = await api.post('/production/batches/', submitData);
        toast.success('Batch created successfully');
      }

      handleCloseDialog();
      fetchBatches();
    } catch (error) {
      console.error('Error saving batch:', error);
      toast.error('Failed to save batch');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this batch?')) {
      try {
        await api.delete(`/production/batches/${id}/`);
        toast.success('Batch deleted successfully');
        fetchBatches();
      } catch (error) {
        toast.error('Failed to delete batch');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'in_progress': 'warning',
      'completed': 'success',
      'cancelled': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'in_progress': 'In Progress',
      'completed': 'Completed',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  };

  const getShiftLabel = (shift) => {
    const labels = {
      'general': 'General Shift',
      'night': 'Night Shift',
      'e1': 'E1 Shift',
      'e2': 'E2 Shift',
      'e3': 'E3 Shift'
    };
    return labels[shift] || shift || 'N/A';
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Production Batch Log
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Manage and track production batches
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchBatches} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Log Production Batch
            </Button>
          </Box>
        </Box>

        {settings && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="textSecondary">
              <strong>Extraction Standards:</strong>
            </Typography>
            <Chip 
              label={`Oil: ${settings.oil_percentage}%`} 
              size="small" 
              sx={{ bgcolor: '#FFD700' }}
            />
            <Chip 
              label={`Cake: ${settings.cake_percentage}%`} 
              size="small" 
              sx={{ bgcolor: '#8B7355', color: '#fff' }}
            />
            <Chip 
              label={`Sludge: ${settings.sludge_percentage}%`} 
              size="small" 
              sx={{ bgcolor: '#808080', color: '#fff' }}
            />
          </Box>
        )}
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Batch #</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Shift</TableCell>
                  <TableCell>Production Date</TableCell>
                  <TableCell align="right">Shell Input (kg)</TableCell>
                  <TableCell align="right">Oil Output (L)</TableCell>
                  <TableCell align="right">Cake Output (kg)</TableCell>
                  <TableCell align="right">Sludge Output (kg)</TableCell>
                  <TableCell>Operator</TableCell>
                  <TableCell>Machine ID</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body1" color="textSecondary">
                        No production batches found. Click "Log Production Batch" to add one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  batches.map((batch) => (
                    <TableRow key={batch.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {batch.batch_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusLabel(batch.status)} 
                          color={getStatusColor(batch.status)} 
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={<ShiftIcon />}
                          label={getShiftLabel(batch.shift)} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CalendarIcon fontSize="small" color="action" />
                          {batch.production_date ? format(new Date(batch.production_date), 'dd/MM/yyyy') : 'N/A'}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{batch.shell_input}</TableCell>
                      <TableCell align="right" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                        {batch.oil_output}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#8B7355', fontWeight: 'bold' }}>
                        {batch.cake_output}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#808080', fontWeight: 'bold' }}>
                        {batch.sludge_output}
                      </TableCell>
                      <TableCell>{batch.operator || 'N/A'}</TableCell>
                      <TableCell>{batch.machine_id || 'N/A'}</TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleOpenDialog(batch)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDelete(batch.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Production Batch Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editingBatch ? 'Edit Production Batch' : 'Log Production Batch'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Batch Number"
                value={formData.batch_number}
                onChange={handleChange('batch_number')}
                required
                helperText="Unique identifier for this batch"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={handleChange('status')}
                  label="Status"
                >
                  {BATCH_STATUSES.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Production Date"
                type="date"
                value={formData.production_date}
                onChange={handleChange('production_date')}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Shift</InputLabel>
                <Select
                  value={formData.shift}
                  onChange={handleChange('shift')}
                  label="Shift"
                >
                  <MenuItem value="">Select Shift</MenuItem>
                  {SHIFT_OPTIONS.map((shift) => (
                    <MenuItem key={shift.value} value={shift.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ShiftIcon fontSize="small" />
                        {shift.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Machine ID"
                value={formData.machine_id}
                onChange={handleChange('machine_id')}
                helperText="e.g., MACH-01, EXTRACTOR-01"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <FactoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Input & Output
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Shell Input (kg)"
                type="number"
                value={formData.shell_input}
                onChange={handleChange('shell_input')}
                required
                helperText="Enter shell input in kilograms"
                InputProps={{
                  inputProps: { min: 0, step: 0.01 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Button
                  variant="outlined"
                  onClick={handleAutoCalculate}
                  startIcon={<CalculateIcon />}
                  sx={{ height: 56, width: '100%' }}
                >
                  Auto-Calculate Outputs
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>Auto-Calculation:</strong> Oil, Cake, and Sludge outputs are automatically calculated 
                  based on the Shell Input and configured extraction percentages.
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Oil Output (L)"
                type="number"
                value={formData.oil_output}
                onChange={handleChange('oil_output')}
                InputProps={{
                  inputProps: { min: 0, step: 0.01 },
                  sx: { bgcolor: '#FFF8E1' }
                }}
                helperText={settings ? `Auto-calculated (${settings.oil_percentage}%)` : 'Enter oil output'}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Cake Output (kg)"
                type="number"
                value={formData.cake_output}
                onChange={handleChange('cake_output')}
                InputProps={{
                  inputProps: { min: 0, step: 0.01 },
                  sx: { bgcolor: '#EFEBE9' }
                }}
                helperText={settings ? `Auto-calculated (${settings.cake_percentage}%)` : 'Enter cake output'}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Sludge Output (kg)"
                type="number"
                value={formData.sludge_output}
                onChange={handleChange('sludge_output')}
                InputProps={{
                  inputProps: { min: 0, step: 0.01 },
                  sx: { bgcolor: '#F5F5F5' }
                }}
                helperText={settings ? `Auto-calculated (${settings.sludge_percentage}%)` : 'Enter sludge output'}
              />
            </Grid>

            {settings && formData.shell_input > 0 && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Based on {formData.shell_input} kg Shell Input:</strong>
                  </Typography>
                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid item xs={4}>
                      <Chip 
                        label={`Oil: ${formData.oil_output} L`} 
                        sx={{ bgcolor: '#FFD700', width: '100%' }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Chip 
                        label={`Cake: ${formData.cake_output} kg`} 
                        sx={{ bgcolor: '#8B7355', color: '#fff', width: '100%' }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Chip 
                        label={`Sludge: ${formData.sludge_output} kg`} 
                        sx={{ bgcolor: '#808080', color: '#fff', width: '100%' }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Operator"
                value={formData.operator}
                onChange={handleChange('operator')}
                helperText="Enter operator name manually if not in list"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={handleChange('notes')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
          >
            {editingBatch ? 'Update' : 'Create'} Batch
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Production;